const chapter = {
  id: 'p-block-14',
  slug: 'p-block-14',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'p-Block — Group 14 (Carbon Family)',
        hindi: 'p-खण्ड — वर्ग 14 (कार्बन परिवार)',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'general',
      title: {
        english: 'General Trends in Group 14 (C, Si, Ge, Sn, Pb)',
        hindi: 'समूह 14 में सामान्य रुझान (सी, सी, जीई, एसएन, पीबी)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Outer config $ns^2 np^2$. Common oxidation states +4 and +2.',
        hindi: 'बाहरी कॉन्फ़िगरेशन $ns^2 np^2$। सामान्य ऑक्सीकरण अवस्थाएँ +4 और +2 हैं।',
            },
            {
              english: 'Character: C, Si non-metals; Ge metalloid; Sn, Pb metals.',
        hindi: 'चरित्र: सी, सी गैर-धातु; जीई मेटलॉइड; एसएन, पीबी धातु।',
            },
            {
              english: 'Atomic radius: C < Si < Ge < Sn < Pb (irregular increase due to $d, f$ contractions).',
        hindi: 'परमाणु त्रिज्या: C < Si < Ge < Sn < Pb ($d, f$ संकुचन के कारण अनियमित वृद्धि)।',
            },
            {
              english: '**Inert-pair effect** down the group: stability of +2 over +4 increases. PbCl₄ is unstable (oxidising), PbCl₂ stable; CCl₄ very stable, no +2.',
        hindi: '**निष्क्रिय-युग्म प्रभाव** समूह में नीचे: +2 से अधिक +4 की स्थिरता बढ़ती है। PbCl₄ अस्थिर (ऑक्सीकरणकारी) है, PbCl₂ स्थिर है; CCl₄ बहुत स्थिर, कोई +2 नहीं।',
            },
            {
              english: '**Catenation** (self-linking) order: C ≫ Si > Ge ≈ Sn ≫ Pb. Reason: very strong C–C bond (348 kJ/mol). Basis of organic chemistry.',
        hindi: '**श्रृंखला** (स्वयं-लिंकिंग) क्रम: सी ≫ सी > जीई ≈ एसएन ≫ पीबी। कारण: बहुत मजबूत C-C बंधन (348 kJ/mol)। कार्बनिक रसायन विज्ञान का आधार.',
            },
            {
              english: 'Tendency to form $p\\pi$–$p\\pi$ multiple bonds: only carbon (C=C, C≡C, C=O). Heavier elements use $d\\pi$–$p\\pi$ (e.g. SiO₄ tetrahedra link via single bonds).',
        hindi: '$p\\pi$–$p\\pi$ एकाधिक बांड बनाने की प्रवृत्ति: केवल कार्बन (C=C, C≡C, C=O)। भारी तत्व $d\\pi$–$p\\pi$ (जैसे एकल बांड के माध्यम से SiO₄ टेट्राहेड्रा लिंक) का उपयोग करते हैं।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Allotropes of carbon — **diamond** (each C $sp^3$, 3-D network, hardest natural substance, electrical insulator), **graphite** ($sp^2$ layers held by van der Waals; conducts within layers; lubricant), **fullerenes** ($C_{60}$ "buckminsterfullerene", soccer-ball, 20 hexagons + 12 pentagons), graphene, carbon nanotubes.',
        hindi: 'कार्बन के अपररूप - **हीरा** (प्रत्येक C $sp^3$, 3-डी नेटवर्क, सबसे कठोर प्राकृतिक पदार्थ, विद्युत इन्सुलेटर), **ग्रेफाइट** ($sp^2$ परतें वैन डेर वाल्स द्वारा धारण की जाती हैं; परतों के भीतर संचालन करती हैं; स्नेहक), **फुलरीन** ($C_{60}$ "बकमिनस्टरफुलरीन", सॉकर-बॉल, 20 हेक्सागोन्स + 12 पेंटागन), ग्राफीन, कार्बन नैनोट्यूब।',
          },
        },
      ],
    },
    {
      id: 'oxides',
      title: {
        english: 'Oxides of Carbon',
        hindi: 'कार्बन के ऑक्साइड',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**CO** — neutral oxide, colourless, odourless, **highly poisonous** (forms carboxyhaemoglobin ~300× more stable than oxyhaemoglobin). Powerful reducing agent (in metallurgy of Fe). Bonding: $|C \\equiv O|$ with lone pair on C; behaves as Lewis base via C → forms metal carbonyls $M(CO)_n$ (synergic σ-donation + π back-bonding).',
        hindi: '**CO** - तटस्थ ऑक्साइड, रंगहीन, गंधहीन, **अत्यधिक जहरीला** (ऑक्सीहीमोग्लोबिन की तुलना में कार्बोक्सीहीमोग्लोबिन ~300× अधिक स्थिर बनाता है)। शक्तिशाली कम करने वाला एजेंट (Fe के धातु विज्ञान में)। बॉन्डिंग: $|C \\equiv O|$ C पर अकेली जोड़ी के साथ; C के माध्यम से लुईस बेस के रूप में व्यवहार करता है → धातु कार्बोनिल्स $M(CO)_n$ (सिनर्जिक σ-दान + π बैक-बॉन्डिंग) बनाता है।',
            },
            {
              english: '**CO₂** — acidic oxide; linear $O=C=O$, $sp$ at C. Greenhouse gas. Solid CO₂ "dry ice". With water: $CO_2 + H_2O \\rightleftharpoons H_2CO_3$ (weak diprotic). Limewater test: $Ca(OH)_2 + CO_2 \\to CaCO_3\\downarrow + H_2O$; excess CO₂ redissolves: $CaCO_3 + CO_2 + H_2O \\to Ca(HCO_3)_2$.',
        hindi: '**CO₂** — अम्लीय ऑक्साइड; C. ग्रीनहाउस गैस पर रैखिक $O=C=O$, $sp$। ठोस CO₂ "सूखी बर्फ"। पानी के साथ: $CO_2 + H_2O \\rightleftharpoons H_2CO_3$ (कमजोर डिप्रोटिक)। चूने का पानी परीक्षण: $Ca(OH)_2 + CO_2 \\to CaCO_3\\downarrow + H_2O$; अतिरिक्त CO₂ पुनः घुल जाता है: $CaCO_3 + CO_2 + H_2O \\to Ca(HCO_3)_2$।',
            },
            {
              english: 'Photosynthesis: $6CO_2 + 6H_2O \\xrightarrow{h\\nu, \\text{chl}} C_6H_{12}O_6 + 6O_2$.',
        hindi: 'प्रकाश संश्लेषण: $6CO_2 + 6H_2O \\xrightarrow{h\\nu, \\text{chl}} C_6H_{12}O_6 + 6O_2$.',
            },
          ],
        },
      ],
    },
    {
      id: 'silicon',
      title: {
        english: 'Silicon — Compounds & Industrial Materials',
        hindi: 'सिलिकॉन — यौगिक और औद्योगिक सामग्री',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Most abundant element in Earth\'s crust **after oxygen** (~27 %); occurs as silica and silicates.',
        hindi: 'पृथ्वी की पपड़ी में सबसे प्रचुर तत्व **ऑक्सीजन के बाद** (~27%); सिलिका और सिलिकेट के रूप में होता है।',
            },
            {
              english: '**SiO₂ (silica)** — covalent network of SiO₄ tetrahedra (each O shared between two Si). Quartz, cristobalite, tridymite. Reacts only with HF: $SiO_2 + 4HF \\to SiF_4 + 2H_2O$.',
        hindi: '**SiO₂ (सिलिका)** - SiO₄ टेट्राहेड्रा का सहसंयोजक नेटवर्क (प्रत्येक O दो Si के बीच साझा किया जाता है)। क्वार्ट्ज़, क्रिस्टोबलाइट, ट्राइडीमाइट। केवल HF के साथ प्रतिक्रिया करता है: $SiO_2 + 4HF \\to SiF_4 + 2H_2O$।',
            },
            {
              english: '**Silicates** — built from $[SiO_4]^{4-}$ tetrahedra. Classes: ortho (single, e.g. olivine), pyro (two sharing one O, $Si_2O_7^{6-}$), cyclic (rings, beryl), chain (single = pyroxenes, double = amphiboles), sheet (mica, talc), 3-D (feldspar, zeolites).',
        hindi: '**सिलिकेट्स** — $[SiO_4]^{4-}$ टेट्राहेड्रा से निर्मित। वर्ग: ऑर्थो (एकल, उदाहरण के लिए ओलिवाइन), पायरो (दो साझा एक O, $Si_2O_7^{6-}$), चक्रीय (रिंग्स, बेरिल), चेन (single = pyroxenes, double = amphiboles), शीट (अभ्रक, टैल्क), 3-डी (फेल्डस्पार, जिओलाइट्स)।',
            },
            {
              english: '**Zeolites** — 3-D aluminosilicates with cavities; used as catalysts in petrochemicals (cracking, ZSM-5), shape-selective catalysis, ion-exchange (water softening).',
        hindi: '**जिओलाइट्स** - गुहाओं के साथ 3-डी एल्युमिनोसिलिकेट्स; पेट्रोकेमिकल (क्रैकिंग, ZSM-5), आकार-चयनात्मक उत्प्रेरक, आयन-विनिमय (पानी नरम करना) में उत्प्रेरक के रूप में उपयोग किया जाता है।',
            },
            {
              english: '**Silicones** — synthetic $[R_2 SiO]_n$ polymers from $R_2SiCl_2$ hydrolysis. Water-repellent, thermally stable, biocompatible (lubricants, sealants, implants).',
        hindi: '**सिलिकॉन** - $R_2SiCl_2$ हाइड्रोलिसिस से सिंथेटिक $[R_2 SiO]_n$ पॉलिमर। जल-विकर्षक, तापीय रूप से स्थिर, जैव-संगत (स्नेहक, सीलेंट, प्रत्यारोपण)।',
            },
            {
              english: '**Silicon carbide (SiC, carborundum)** — extremely hard, abrasive; made by heating quartz with coke at ~2500 K.',
        hindi: '**सिलिकॉन कार्बाइड (SiC, कार्बोरंडम)** - अत्यंत कठोर, अपघर्षक; ~2500 K पर कोक के साथ क्वार्ट्ज को गर्म करके बनाया गया।',
            },
          ],
        },
      ],
    },
    {
      id: 'tin-lead',
      title: {
        english: 'Tin & Lead',
        hindi: 'टिन और सीसा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Sn: silvery; ores **cassiterite** SnO₂. Pb: bluish-grey, soft; ore **galena** PbS.',
        hindi: 'एसएन: चांदी; अयस्क **कैसिटराइट** SnO₂। पंजाब: नीला-भूरा, मुलायम; अयस्क **गैलेना** पीबीएस।',
            },
            {
              english: 'Allotropes of Sn: white tin (β, metallic, stable above 286 K) ↔ grey tin (α, brittle, below 286 K — "tin pest").',
        hindi: 'Sn के अपरूप: सफेद टिन (β, धात्विक, 286 K से ऊपर स्थिर) ↔ ग्रे टिन (α, भंगुर, 286 K से नीचे - "टिन कीट")।',
            },
            {
              english: '+2 of Sn is reducing ($SnCl_2$ reduces $HgCl_2$); +4 of Pb is oxidising ($PbO_2$ used in lead-acid battery).',
        hindi: 'Sn का +2 कम हो रहा है ($SnCl_2$ $HgCl_2$ कम कर देता है); Pb का +4 ऑक्सीकरण कर रहा है ($PbO_2$ लेड-एसिड बैटरी में उपयोग किया जाता है)।',
            },
            {
              english: 'Lead-acid battery: $Pb + PbO_2 + 2H_2SO_4 \\to 2PbSO_4 + 2H_2O$ (discharge); reverse on charging.',
        hindi: 'लेड-एसिड बैटरी: $Pb + PbO_2 + 2H_2SO_4 \\to 2PbSO_4 + 2H_2O$ (डिस्चार्ज); चार्ज करने पर उल्टा।',
            },
            {
              english: 'Toxicity: Pb²⁺ replaces Ca²⁺ in bones; symptoms include neurological damage. Banned in petrol (tetraethyl-lead) and in paints.',
        hindi: 'विषाक्तता: Pb²⁺ हड्डियों में Ca²⁺ का स्थान ले लेता है; लक्षणों में तंत्रिका संबंधी क्षति शामिल है। पेट्रोल (टेट्राएथिल-लेड) और पेंट में प्रतिबंधित।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Why is PbCl₄ less stable than PbCl₂, while CCl₄ is stable?',
        hindi: 'PbCl₄ PbCl₂ से कम स्थिर क्यों है, जबकि CCl₄ स्थिर है?',
          },
          solution: {
            english: 'Inert-pair effect: in heavier members of Gp 14, $6s^2$ pair becomes inert; +2 state is preferred over +4. PbCl₄ tends to decompose to PbCl₂ + Cl₂. Carbon, having no inert-pair effect, exhibits +4 exclusively.',
        hindi: 'निष्क्रिय-जोड़ी प्रभाव: जीपी 14 के भारी सदस्यों में, $6s^2$ जोड़ी निष्क्रिय हो जाती है; +4 की तुलना में +2 अवस्था को प्राथमिकता दी जाती है। PbCl₄ PbCl₂ + Cl₂ में विघटित हो जाता है। कार्बन, जिसका कोई अक्रिय-युग्म प्रभाव नहीं है, विशेष रूप से +4 प्रदर्शित करता है।',
          },
        },
      ],
    },
    {
      id: 'allotropes-summary',
      title: {
        english: 'Useful Comparisons',
        hindi: 'उपयोगी तुलनाएँ',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Property', 'Diamond', 'Graphite', 'Fullerene ($C_{60}$)'],
          rows: [
            ['Hybridisation', '$sp^3$', '$sp^2$', '$sp^2$'],
            ['Structure', '3-D network', 'Layered hexagonal', 'Cage of 20 hexagons + 12 pentagons'],
            ['C–C bond length', '154 pm', '141.5 pm (in layer)', '143.5 (6:6) / 138.3 (6:5) pm'],
            ['Electrical conductivity', 'Insulator', 'Conducts (along layer)', 'Semi-conductor'],
            ['Density (g/cm³)', '3.51', '2.22', '1.65'],
            ['Use', 'Abrasive, jewellery', 'Lubricant, electrodes', 'Superconductors, drug delivery'],
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'CO_2 + H_2O \\rightleftharpoons H_2CO_3',
      desc: {
        english: 'Acidic carbon oxide',
        hindi: 'अम्लीय कार्बन ऑक्साइड',
      },
    },
    {
      latex: 'Ca(OH)_2 + CO_2 \\to CaCO_3\\downarrow + H_2O',
      desc: {
        english: 'Limewater test',
        hindi: 'लाइमवाटर परीक्षण',
      },
    },
    {
      latex: 'CaCO_3 + CO_2 + H_2O \\to Ca(HCO_3)_2',
      desc: {
        english: 'Excess CO₂ redissolves',
        hindi: 'अतिरिक्त CO₂ पुनः घुल जाता है',
      },
    },
    {
      latex: 'SiO_2 + 4HF \\to SiF_4 + 2H_2O',
      desc: {
        english: 'Etching of glass',
        hindi: 'कांच की नक्काशी',
      },
    },
    {
      latex: 'Pb + PbO_2 + 2H_2SO_4 \\to 2PbSO_4 + 2H_2O',
      desc: {
        english: 'Lead-acid battery (discharge)',
        hindi: 'लेड-एसिड बैटरी (डिस्चार्ज)',
      },
    },
    {
      latex: '[R_2SiO]_n',
      desc: {
        english: 'Silicone polymer repeat unit',
        hindi: 'सिलिकॉन पॉलिमर रिपीट यूनिट',
      },
    },
    {
      latex: '\\text{Catenation:}\\ C \\gg Si > Ge \\approx Sn \\gg Pb',
      desc: {
        english: 'Self-linking trend',
        hindi: 'सेल्फ-लिंकिंग प्रवृत्ति',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Inert pair',
      problem: {
        english: 'Order of stability of +2 oxidation state in Gp 14:',
        hindi: 'जीपी 14 में +2 ऑक्सीकरण अवस्था की स्थिरता का क्रम:',
      },
      answer: {
        english: 'C < Si < Ge < Sn < Pb',
        hindi: 'C < Si < Ge < Sn < Pb',
      },
      solution: {
        english: 'Increases down the group due to inert-pair effect.',
        hindi: 'अक्रिय-युग्म प्रभाव के कारण समूह में नीचे की ओर वृद्धि होती है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Allotrope',
      problem: {
        english: 'Hybridisation of carbon atoms in fullerene $C_{60}$:',
        hindi: 'फुलरीन में कार्बन परमाणुओं का संकरण $C_{60}$:',
      },
      answer: {
        english: '$sp^2$',
        hindi: '$sp^2$',
      },
      solution: {
        english: 'Cage of pentagons & hexagons with $sp^2$ carbons.',
        hindi: '$sp^2$ कार्बन के साथ पेंटागन और हेक्सागोन का पिंजरा।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Silicates',
      problem: {
        english: 'Mica and talc are which class of silicates?',
        hindi: 'अभ्रक और टैल्क सिलिकेट के किस वर्ग के हैं?',
      },
      answer: {
        english: 'Sheet (phyllo) silicates',
        hindi: 'शीट (फाइलो) सिलिकेट्स',
      },
      solution: {
        english: '2-D sharing of three O per SiO₄.',
        hindi: 'प्रति SiO₄ तीन O का 2-डी साझाकरण।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'CO',
      problem: {
        english: 'CO is poisonous because it:',
        hindi: 'CO जहरीली है क्योंकि:',
      },
      answer: {
        english: 'Binds Hb 300× more strongly than O₂ to form carboxyhaemoglobin',
        hindi: 'कार्बोक्सीहीमोग्लोबिन बनाने के लिए Hb को O₂ की तुलना में 300× अधिक मजबूती से बांधता है',
      },
      solution: {
        english: 'Blocks oxygen transport.',
        hindi: 'ऑक्सीजन परिवहन को अवरुद्ध करता है।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'SiO₂',
      problem: {
        english: 'Glass is etched by:',
        hindi: 'कांच पर नक्काशी की जाती है:',
      },
      answer: {
        english: 'HF',
        hindi: 'एचएफ',
      },
      solution: {
        english: '$SiO_2 + 4HF \\to SiF_4 + 2H_2O$.',
        hindi: '$SiO_2 + 4HF \\to SiF_4 + 2H_2O$.',
      },
    },
  ],
  next: {
    slug: 'solid-state',
    title: 'The Solid State',
  },
};

export default chapter;
